import React, { useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { StocktakingApi } from "src/services/Api/Organization/types";
import { getOrderNotClosedConfig } from "src/utils/ordersNotClosedAlert";
import { ExpandOpenCorrections, OrderNotClosedApiError } from "src/utils/types";
import { selectOrganization } from "go-security/services/organizations/selectors";

interface Props {
	orderNotClosedError: OrderNotClosedApiError;
	className?: string;
	stocktaking?: StocktakingApi;
}

const getExpandedCorrectionsInitalValue = (openCorrections?: Record<string, any>[]): ExpandOpenCorrections => {
	if (!openCorrections || openCorrections.length < 5) return ExpandOpenCorrections.INVISIBLE;

	return ExpandOpenCorrections.NOT_EXPANDED;
};

export const OrdersNotClosedAlert = ({ orderNotClosedError, className, stocktaking }: Props) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const openCorrections = orderNotClosedError.documents?.PURCHASE_CORRECTION;
	const [expandOpenCorrections, setExpandOpenCorrections] = useState<ExpandOpenCorrections>(() =>
		getExpandedCorrectionsInitalValue(openCorrections)
	);
	const limitedOpenCorrectionsArray: Record<string, any>[] | undefined =
		expandOpenCorrections === ExpandOpenCorrections.NOT_EXPANDED ? openCorrections?.slice(0, 5) : openCorrections;
	const notClosedOrdersConfig = getOrderNotClosedConfig(
		t,
		orderNotClosedError.document_types,
		organization.id,
		stocktaking
	);

	const onExpandCorrectionsClick = () => {
		setExpandOpenCorrections((prevExpandOpenCorrections) =>
			prevExpandOpenCorrections === ExpandOpenCorrections.NOT_EXPANDED
				? ExpandOpenCorrections.EXPANDED
				: ExpandOpenCorrections.NOT_EXPANDED
		);
	};

	const getExpandCorrectionsTranslation = (): string | null => {
		if (expandOpenCorrections === ExpandOpenCorrections.INVISIBLE) {
			return null;
		}

		return `[${t(`common.action.${expandOpenCorrections === ExpandOpenCorrections.EXPANDED ? "less" : "more"}`, {
			ns: "lib",
		})}]`;
	};

	return (
		<Alert variant="warning" className={className}>
			{notClosedOrdersConfig.map(({ translation, redirectLink, documentType }) => {
				const correctionsNotClosed = documentType === "PURCHASE_CORRECTION";

				if (correctionsNotClosed && limitedOpenCorrectionsArray && limitedOpenCorrectionsArray.length > 0) {
					return (
						<React.Fragment key={documentType}>
							{translation}
							{limitedOpenCorrectionsArray?.map(
								({ number, id, parent_id: purchaseId }, index: number) => {
									return (
										<Link
											key={id}
											to={`/${organization.id}/documents/purchases/${purchaseId}/corrections/${id}`}
											target="_blank"
											rel="noreferrer"
											className="ms-1"
										>
											{`${number}${index === limitedOpenCorrectionsArray.length - 1 ? "" : ","}`}
										</Link>
									);
								}
							)}
							<button onClick={onExpandCorrectionsClick} className="btn-with-link-styling ms-2">
								{getExpandCorrectionsTranslation()}
							</button>
							<br />
						</React.Fragment>
					);
				}

				if (redirectLink) {
					return (
						<React.Fragment key={documentType}>
							<Link to={redirectLink} target="_blank" rel="noreferrer">
								{translation}
							</Link>
							<br />
						</React.Fragment>
					);
				}

				return translation;
			})}
		</Alert>
	);
};
